import type { RequestLinkInput, UpdateProfileInput, UserDto } from '@miroom/shared';
import { create } from 'zustand';
import type { AuthApi } from '../ports/api';
import { usePrefs } from './prefsStore';

export type SessionStatus = 'unknown' | 'guest' | 'authenticated';

interface SessionState {
  status: SessionStatus;
  user: UserDto | null;
  setUser(user: UserDto | null): void;
}

export const useSession = create<SessionState>((set) => ({
  status: 'unknown',
  user: null,
  setUser: (user) => set({ user, status: user ? 'authenticated' : 'guest' }),
}));

/** Applies profile preferences to this device after sign-in. */
function adoptProfilePrefs(user: UserDto) {
  const prefs = usePrefs.getState();
  if (prefs.locale !== user.locale) prefs.setLocale(user.locale);
  if (prefs.theme !== user.theme) prefs.setTheme(user.theme);
}

/** Session use cases bound to an AuthApi implementation. */
export function sessionActions(api: AuthApi) {
  const { setUser } = useSession.getState();
  return {
    async refresh() {
      let user: UserDto | null = null;
      try {
        user = await api.me();
      } catch {
        // Offline or server down: treat as guest, pages show their own errors.
      }
      // A sign-in that finished meanwhile (e.g. an emailed link) wins over this older answer.
      if (useSession.getState().status !== 'unknown') return;
      setUser(user);
      if (user) adoptProfilePrefs(user);
    },
    /** The email comes in the language chosen on this device. */
    async requestLink(input: Omit<RequestLinkInput, 'locale'>) {
      await api.requestLink({ ...input, locale: usePrefs.getState().locale });
    },
    /** Signs in with an emailed link; returns where to continue. */
    async verifyLink(token: string) {
      const { user: signedIn, next, created } = await api.verifyLink(token);
      let user = signedIn;
      if (created) {
        // New accounts take over the language/theme chosen on this device.
        const prefs = usePrefs.getState();
        if (user.locale !== prefs.locale || user.theme !== prefs.theme) {
          user = await api.updateProfile({ locale: prefs.locale, theme: prefs.theme });
        }
      } else {
        adoptProfilePrefs(user);
      }
      setUser(user);
      return next;
    },
    async logout() {
      await api.logout().catch(() => undefined);
      setUser(null);
    },
    async logoutAll() {
      await api.logoutAll();
      setUser(null);
    },
    async updateProfile(patch: UpdateProfileInput) {
      const user = await api.updateProfile(patch);
      setUser(user);
      return user;
    },
    async deleteAccount(email: string) {
      await api.deleteAccount(email);
      setUser(null);
    },
  };
}
